import React, { useContext, useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import { ALERT_TYPE, COMPANY_STATUS_VALUE, USER_ROLE, VALIDATIONS, ALIGN, POSITIONS, USER_ROLE_VALUES } from '../../constants';
import { optionValues, omitProps, removeHyphens } from '../../utils/utils';
import { acquireAccessToken } from "../../services/api/auth";
import { protectedResources } from "../../msalAuthConfig";
import { getCurrentUser, getUsers } from "../../ducks/user";
import { ApiInstance } from "../../views/admin/user/UserManagement";
import { useDispatch, useSelector } from "react-redux";
import { useMsal } from "../../auth/msalExports";
import messages from "../../utils/helper/messages";
import { AlertContext } from "../../context/AlertContext";
import SharedForm from './Form';

export const UserProfileModal = ({ showModal, selectedUser, inputChanged, onHideCallback, onFormSubmitCallback, isReadOnly, isDirty = false }) => {
    const dispatch = useDispatch();
    const { instance } = useMsal();
    const { showAlert } = useContext(AlertContext);

    const [isLoading, setIsLoading] = useState(false);
    const [roleOptions, setRoleOptions] = useState([]);
    const currentUser = useSelector((state) => state.user.currentUser);

    useEffect(() => {
        setRoleOptions(optionValues((isReadOnly ||
            (
                currentUser?.roleId === USER_ROLE.SYSTEM_ADMIN &&
                currentUser?.companyRecordStatus === COMPANY_STATUS_VALUE.Permanent &&
                currentUser?.companyId === selectedUser.companyId
            )
        ) ? omitProps(USER_ROLE_VALUES, USER_ROLE_VALUES[1]) : USER_ROLE_VALUES, true));
    }, [currentUser]);

    const arr = [
        { key: 'firstName', labelName: 'First Name', placeholderName: 'First Name', type: 'text', valid: VALIDATIONS.String.isName, disabled: isReadOnly },
        { key: 'lastName', labelName: 'Last Name', placeholderName: 'Last Name', type: 'text', valid: VALIDATIONS.String.isName, disabled: isReadOnly },
        { key: 'email', labelName: 'Email', placeholderName: 'Email', type: 'email', disabled: isReadOnly, formText: !isReadOnly ? 'Updating this value will require the user to re-auth.' : null },
        { key: 'phone', labelName: 'Phone Number', placeholderName: 'Phone Number', type: 'tel', maxLength: 12, valid: VALIDATIONS.Number.isPhone, disabled: isReadOnly, formText: !isReadOnly ? 'Updating this value will require the user to re-auth.' : null },
        { key: 'roleId', labelName: 'User Role', type: 'options', optionValues: roleOptions }
    ];

    const submitCallback = async (formDataObj) => {
        try {
            setIsLoading(true);
            let accessToken = await acquireAccessToken(instance);
            ApiInstance.setAuth(accessToken);
            ApiInstance.setContext(showAlert);
            let result = await ApiInstance({
                method: 'PUT',
                url: `${protectedResources.apiUsers.endpoint}/update`,
                data: {
                    userData: {
                        ...formDataObj,
                        id: selectedUser.id,
                        recordStatus: selectedUser.recordStatus,
                        phone: removeHyphens(formDataObj.phone)
                    }
                }
            });
            if (result.data) {
                onFormSubmitCallback(result.data);
                showAlert(messages.success.updateUser, ALERT_TYPE.SUCCESS);

                // update user initials if current user
                if (Number(selectedUser.id) === currentUser.id) {
                    dispatch(getCurrentUser(instance));
                }

                // fetch and refresh users
                dispatch(getUsers(instance));

                onHideCallback();
            }
            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
            showAlert(`${messages.error.prefix} ${error?.response?.data?.error || error}.`, ALERT_TYPE.ERROR);
        }
    };


    return (
        <Modal
            show={showModal}
            fullscreen={false}
            backdrop="static"
            onHide={onHideCallback}
            size="md">
            <Modal.Header closeButton>
                <Modal.Title>{isReadOnly ? 'User Profile' : 'Edit User'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="px-xl-3 py-xl-2">
                    {<SharedForm
                        cols={12}
                        array={arr}
                        modelObj={selectedUser}
                        onSubmit={submitCallback}
                        onInputChanged={inputChanged}
                        loading={isLoading}
                        disabled={isLoading || !isDirty}
                        actionBarPosition={POSITIONS.BOTTOM}
                        actionBarAlign={ALIGN.LEFT}
                        viewOnly={isReadOnly}
                        submitButtonText="Update"
                        showCancel={false}
                    />}
                </div>
            </Modal.Body>
        </Modal>
    );
};
